import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CreateAndEditProjectModal from '@features/project-management-tools/components/CreateAndEditProjectModal';
import MyProjectKanbanBoard from '@features/project-management-tools/components/MyProjectKanbanBoard';
import MyProjectTopbar from '@features/project-management-tools/components/MyProjectTopbar';
import {
	useDeleteProjectMutation,
	useGetAllProjectsMutation,
	useGetMyProjectFilterOptionsMutation,
	useGetProjectCardViewMutation,
	useGetProjectListViewMutation
} from '@features/project-management-tools/api/projectManagementApi';
import {
	setActiveTab,
	setCompletedProjects,
	setInProgressProjects,
	setIsEditProject,
	setIsShowCreateProjectModal,
	setIsShowDeleteProjectModal,
	setNotStartedProjects,
	setSelectedProjectIdToDelete,
	setSelectedProjectToEdit
} from '@features/project-management-tools/redux/myProjectSlice';
import { createFilterRequestBody } from '@features/project-management-tools/utils';
import TaskManagementCalendar from '@features/task-management-calendar/TaskManagementCalendar';
import ViewProjectDetailsModal from '@features/task-management-calendar/components/ViewProjectDetailsModal';
import { PROJECT_TAB_VIEW_TYPES } from '@features/project-management-tools/constents';
import ProjectTableView from '@features/project-management-tools/components/ProjectTableView';
import ProjectCardsView from '@features/project-management-tools/components/ProjectCardsView';

import { setSystemUserPreferences } from '@services/commonService';

import DeleteConfirmationModal from '@/components/DeleteConfirmationModal';
import { toastError, toastSuccess } from '@/components/Toast';
import { useMyProject } from '@/store/reducers';
import { capitalizeFirstLetter, getUserPreference, isPageReachBottom } from '@/utils/Util';
import FilterComponent from '@/features/filter/v2/FilterComponent';
import { allFilterPages, myProjectsFilterFields } from '@/features/filter/static/filterUtil';
import { Types } from '@/store/types';

const initialStateForProjects = {
	notStartedProjects: { data: [], totalElements: 0, currentPage: 0 },
	inProgressProjects: { data: [], totalElements: 0, currentPage: 0 },
	completedProjects: { data: [], totalElements: 0, currentPage: 0 },
	selectedProjectToEdit: null
};

const MyProjectPage = () => {
	const { activeTab } = useMyProject();
	const [page, setPage] = useState(0);
	const [listViewPage, setListViewPage] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [isListViewLoadingMore, setIsListViewLoadingMore] = useState(false);
	const isLoadingRef = useRef(false);
	const dispatch = useDispatch();
	const [isShowFilter, setIsShowFilter] = useState(false);
	const [search, setSearch] = useState('');
	const filterfields = useSelector((store) => store.filterFieldStore);
	const [currentDate, setCurrentDate] = useState(new Date());
	const [viewMode, setViewMode] = useState('Month');
	const [isShowViewProjectDetailsModal, setIsShowViewProjectDetailsModal] = useState(false);
	const [selectedEventId, setSelectedEventId] = useState(null);
	const [selectedSort, setSelectedSort] = useState('BRAND');
	const [isDescending, setIsDescending] = useState(false);
	const [accumulatedListViewData, setAccumulatedListViewData] = useState([]); // Table view Data

	// All Projects Api KANBAN
	const [getAllProjects, { data: allProjectsList }] = useGetAllProjectsMutation({ page, limit: 20 });
	const [getMyProjectFilterOptions] = useGetMyProjectFilterOptionsMutation();
	// Project Card View Api CARD
	const [getProjectCardView, { data: projectCardView, isLoading: projectLoading }] =
		useGetProjectCardViewMutation();

	// Project List View Api LIST
	const [getProjectListView, { data: projectListView, isLoading: projectListViewLoading }] =
		useGetProjectListViewMutation();

	// Initialize activeTab from user preferences
	useEffect(() => {
		const userPreferredView = getUserPreference()?.MY_PROJECT_VIEW_TYPE || PROJECT_TAB_VIEW_TYPES.KANBAN;
		if (activeTab !== userPreferredView) {
			dispatch(setActiveTab(userPreferredView));
		}
	}, []);

	const {
		isShowCreateProjectModal,
		isEditProject,
		isShowDeleteProjectModal,
		selectedProjectIdToDelete,
		selectedProjectToEdit
	} = useMyProject();

	const [deleteProject] = useDeleteProjectMutation();
	const projectContainerRef = useRef(null);
	const pendingProjectsRef = useRef(initialStateForProjects.notStartedProjects);
	const inProgressProjectsRef = useRef(initialStateForProjects.inProgressProjects);
	const completedProjectsRef = useRef(initialStateForProjects.completedProjects);

	// reset all project states and refs
	const resetProjectsStateAndRefs = () => {
		setPage(0);
		pendingProjectsRef.current = { ...initialStateForProjects.notStartedProjects };
		inProgressProjectsRef.current = { ...initialStateForProjects.inProgressProjects };
		completedProjectsRef.current = { ...initialStateForProjects.completedProjects };
		dispatch(setNotStartedProjects({ ...initialStateForProjects.notStartedProjects }));
		dispatch(setInProgressProjects({ ...initialStateForProjects.inProgressProjects }));
		dispatch(setCompletedProjects({ ...initialStateForProjects.completedProjects }));
		isLoadingRef.current = false;
	};

	useEffect(() => {
		isLoadingRef.current = isLoading;
	}, [isLoading]);

	const getStatusList = () => {
		if (filterfields?.statusList?.length > 0) {
			return filterfields.statusList.map((status) => status.key);
		}

		return activeTab === PROJECT_TAB_VIEW_TYPES.CARD
			? ['PENDING', 'RUNNING']
			: ['PENDING', 'RUNNING', 'COMPLETED'];
	};

	const getProjectViewType = () => {
		if (activeTab === PROJECT_TAB_VIEW_TYPES.CARD) {
			return 'BRAND_WISE_CARD';
		}
		if (activeTab === PROJECT_TAB_VIEW_TYPES.LIST) {
			return 'LIST';
		}
		if (activeTab === PROJECT_TAB_VIEW_TYPES.KANBAN) {
			return 'KANBAN';
		}
		if (activeTab === PROJECT_TAB_VIEW_TYPES.CALENDAR) {
			return 'CALENDAR';
		}
		return undefined;
	};

	const getYearBoundaries = (date) => {
		if (activeTab === 'CALENDAR' && !filterfields['projectDueDate']?.[0]?.key) {
			const year = date.getFullYear();

			const dueDateStart = `${year}-01-01`;
			const dueDateEnd = `${year}-12-31`;

			return {
				dueDateStart,
				dueDateEnd
			};
		} else {
			return {};
		}
	};

	useEffect(() => {
		if (activeTab === PROJECT_TAB_VIEW_TYPES.KANBAN || activeTab === PROJECT_TAB_VIEW_TYPES.CALENDAR) {
			const requestBody = createFilterRequestBody(filterfields);

			getAllProjects({
				body: {
					statusList: getStatusList(),
					search,
					...requestBody,
					...getYearBoundaries(currentDate)
				},
				params: { page, size: activeTab === PROJECT_TAB_VIEW_TYPES.CALENDAR ? 500 : 20 }
			})
				.unwrap()
				.then((response) => {
					const pending = response?.PENDING || { data: [] };
					const running = response?.RUNNING || { data: [] };
					const completed = response?.COMPLETED || { data: [] };

					updateProjectsRef(pending, pendingProjectsRef);
					updateProjectsRef(running, inProgressProjectsRef);
					updateProjectsRef(completed, completedProjectsRef);
					setIsLoading(false);
					isLoadingRef.current = false;
				})
				.catch((error) => {
					toastError(error?.data?.message || 'Something went wrong!');
					setIsLoading(false);
					isLoadingRef.current = false;
				});
		}
	}, [page, search, filterfields, activeTab, currentDate.getFullYear()]);

	// Reset the state when the component unmounts
	useEffect(() => {
		return () => {
			resetProjectsStateAndRefs();
		};
	}, []);

	const updateProjectsRef = (data, currentRef) => {
		if (currentRef?.totalPages === page) return;

		if (page > 0) {
			currentRef.current = {
				...data,
				data: [...currentRef.current.data, ...data.data]
			};
		} else {
			currentRef.current = data;
		}
	};

	const handleConfirmDeleteProject = () => {
		deleteProject(selectedProjectIdToDelete)
			.unwrap()
			.then(() => {
				setPage(0);
				getAllProjects({
					body: {
						statusList: getStatusList(),
						...getYearBoundaries(currentDate)
					},
					params: { page: 0, size: activeTab === PROJECT_TAB_VIEW_TYPES.CALENDAR ? 500 : 20 }
				});
				toastSuccess('Project deleted successfully');
				dispatch(setIsShowDeleteProjectModal(false));
				dispatch(setSelectedProjectIdToDelete(null));
			})
			.catch((error) => {
				toastError(error);
			});
	};

	const handleListViewLoadMore = () => {
		if (!isListViewLoadingMore && hasMoreListViewData) {
			setIsListViewLoadingMore(true);
			setListViewPage((prevPage) => prevPage + 1);
		}
	};

	const hasMoreListViewData = useMemo(() => {
		return (
			projectListView?.totalElements > accumulatedListViewData.length &&
			listViewPage < projectListView?.totalPages - 1
		);
	}, [projectListView, accumulatedListViewData, listViewPage]);

	// KANBAN scroll handling
	const handleScroll = () => {
		if (isPageReachBottom() && !isLoadingRef.current) {
			if (activeTab === PROJECT_TAB_VIEW_TYPES.KANBAN) {
				// KANBAN view pagination
				const notStartedHasMore =
					pendingProjectsRef.current?.totalElements > pendingProjectsRef.current?.data?.length &&
					page < pendingProjectsRef.current?.totalPages - 1;

				const inProgressHasMore =
					inProgressProjectsRef.current?.totalElements >
						inProgressProjectsRef.current?.data?.length &&
					page < inProgressProjectsRef.current?.totalPages - 1;

				const completedHasMore =
					completedProjectsRef.current?.totalElements >
						completedProjectsRef.current?.data?.length &&
					page < completedProjectsRef.current?.totalPages - 1;

				if (notStartedHasMore || inProgressHasMore || completedHasMore) {
					setIsLoading(true);
					isLoadingRef.current = true;
					setPage((prevPage) => prevPage + 1);
				}
			}
		}
	};

	useEffect(() => {
		// KANBAN scroll handling
		if (activeTab === PROJECT_TAB_VIEW_TYPES.KANBAN) {
			document.addEventListener('scroll', handleScroll);
		}
		return () => {
			document.removeEventListener('scroll', handleScroll);
		};
	}, [activeTab, page]);

	useEffect(() => {
		if (isShowFilter) {
			const requestBody = createFilterRequestBody(filterfields);

			if (Object.keys(requestBody)?.length > 0) {
				resetProjectsStateAndRefs();
			}
			getMyProjectFilterOptions({
				body: {
					...requestBody,
					projectViewType: getProjectViewType(),
					statusList:
						activeTab === PROJECT_TAB_VIEW_TYPES.CARD
							? ['PENDING', 'RUNNING']
							: ['PENDING', 'RUNNING', 'COMPLETED']
				}
			})
				.unwrap()
				.then((response) => {
					dispatch({
						type: Types.STORE_DEPENDENT_FILTER_LIST,
						payload: {
							responseData: {
								...response,
								projectStatusList: response.projectStatusList.map((item) => ({
									name: capitalizeFirstLetter(item.name),
									constant: item.status,
									count: item.count,
									id: item.status
								}))
							},
							type: 'MY_PROJECTS'
						}
					});
				})
				.catch((error) => {
					toastError(error?.data?.message || 'Something went wrong!');
				});
		}
	}, [isShowFilter, filterfields, activeTab]);

	const handleFilterToggle = () => {
		setIsShowFilter((prev) => !prev);
	};

	const handlePrevious = () => {
		if (viewMode === 'Day') {
			const newDate = new Date(currentDate);
			newDate.setDate(newDate.getDate() - 1);
			setCurrentDate(newDate);
		} else if (viewMode === 'Week') {
			const newDate = new Date(currentDate);
			newDate.setDate(newDate.getDate() - 7);
			setCurrentDate(newDate);
		} else if (viewMode === 'Month') {
			setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
		} else if (viewMode === 'Year') {
			setCurrentDate(new Date(currentDate.getFullYear() - 1, currentDate.getMonth(), 1));
		}
	};

	const handleNext = () => {
		if (viewMode === 'Day') {
			const newDate = new Date(currentDate);
			newDate.setDate(newDate.getDate() + 1);
			setCurrentDate(newDate);
		} else if (viewMode === 'Week') {
			const newDate = new Date(currentDate);
			newDate.setDate(newDate.getDate() + 7);
			setCurrentDate(newDate);
		} else if (viewMode === 'Month') {
			setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
		} else if (viewMode === 'Year') {
			setCurrentDate(new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), 1));
		}
	};

	const handleToday = () => {
		setCurrentDate(new Date());
		setViewMode('Day');
	};

	const handleSelectedDate = (date) => {
		setCurrentDate(date);
		setViewMode('Day');
	};

	const handleCalendarEvent = (projectId) => {
		setSelectedEventId(projectId);
		setIsShowViewProjectDetailsModal(true);
	};

	const closeCalendarEvent = () => {
		setSelectedEventId(null);
		setIsShowViewProjectDetailsModal(false);
	};

	useEffect(() => {
		(async () => {
			const userPreference = getUserPreference();
			const body = {
				...userPreference,
				MY_PROJECT_VIEW_TYPE: activeTab
			};
			await setSystemUserPreferences(body);
		})();
	}, [activeTab]);

	// Reset LIST view data when filters/search/sort changes
	useEffect(() => {
		if (activeTab === PROJECT_TAB_VIEW_TYPES.LIST) {
			setListViewPage(0);
			setAccumulatedListViewData([]);
		}
	}, [activeTab, filterfields, search, selectedSort]);

	useEffect(() => {
		if (activeTab === PROJECT_TAB_VIEW_TYPES.LIST) {
			getProjectListView({
				body: { search, statusList: getStatusList(), ...createFilterRequestBody(filterfields) },
				params: { page: listViewPage, size: 500, sortingColumn: selectedSort, isDescending }
			})
				.unwrap()
				.then((response) => {
					if (listViewPage === 0) {
						setAccumulatedListViewData(response.data || []);
					} else {
						setAccumulatedListViewData((prev) => [...prev, ...(response.data || [])]);
					}
					setIsLoading(false);
					setIsListViewLoadingMore(false);
					isLoadingRef.current = false;
				})
				.catch((error) => {
					toastError(error?.data?.message || 'Something went wrong!');
					setIsLoading(false);
					setIsListViewLoadingMore(false);
					isLoadingRef.current = false;
				});
		}
	}, [activeTab, filterfields, search, getProjectListView, listViewPage, selectedSort, isDescending]);

	useEffect(() => {
		if (activeTab === PROJECT_TAB_VIEW_TYPES.CARD) {
			getProjectCardView({
				body: {
					search,
					statusList: getStatusList(),
					...createFilterRequestBody(filterfields)
				}
			})
				.unwrap()
				.then(() => {})
				.catch((error) => {
					toastError(error?.data?.message || 'Something went wrong!');
				});
		}
	}, [activeTab, filterfields, search, getProjectCardView]);

	const getTotalElementCount = () => {
		if (activeTab === PROJECT_TAB_VIEW_TYPES.LIST) {
			return projectListView?.totalElements || 0;
		} else if (activeTab === PROJECT_TAB_VIEW_TYPES.CARD) {
			return projectCardView?.totalElements || 0;
		} else {
			return (
				(allProjectsList?.PENDING?.totalElements || 0) +
				(allProjectsList?.RUNNING?.totalElements || 0) +
				(allProjectsList?.COMPLETED?.totalElements || 0)
			);
		}
	};

	return (
		<>
			<MyProjectTopbar
				projectCount={getTotalElementCount()}
				onOpenCreateProjectModal={() => dispatch(setIsShowCreateProjectModal(true))}
				onFilterToggle={handleFilterToggle}
				onSearch={setSearch}
				currentDate={currentDate}
				onPreviousMonth={handlePrevious}
				onNextMonth={handleNext}
				onToday={handleToday}
				viewMode={viewMode}
				onViewModeChange={setViewMode}
				setProjectView={(value) => dispatch(setActiveTab(value))}
				projectView={activeTab}
			/>
			<section
				className="filter-data-container filter-v3-data-container bg-white task-content"
				ref={projectContainerRef}
			>
				<FilterComponent
					filterPageName={allFilterPages.MY_PROJECTS}
					fields={myProjectsFilterFields}
					showFilter={isShowFilter}
					isDependentFilter={true}
				/>
				<div className="data-table">
					{/* Calendar View tab*/}
					{activeTab === PROJECT_TAB_VIEW_TYPES.CALENDAR && (
						<TaskManagementCalendar
							events={allProjectsList}
							currentDate={currentDate}
							viewMode={viewMode}
							handleEvent={handleCalendarEvent}
							handleSelectedDate={handleSelectedDate}
						/>
					)}

					{/* Kanban View tab*/}
					{activeTab === PROJECT_TAB_VIEW_TYPES.KANBAN && (
						<div className="task-list-container">
							<div className="task-all-row d-grid gap-5 grid-columns-4">
								<MyProjectKanbanBoard />
							</div>
						</div>
					)}

					{/* List View tab*/}
					{activeTab === PROJECT_TAB_VIEW_TYPES.LIST && (
						<ProjectTableView
							projectListView={{
								...projectListView,
								data: accumulatedListViewData
							}}
							projectListViewLoading={projectListViewLoading}
							setSelectedSort={setSelectedSort}
							selectedSort={selectedSort}
							refetchTasks={() => {
								setListViewPage(0);
								setAccumulatedListViewData([]);
								getProjectListView({
									body: { search, ...createFilterRequestBody(filterfields) },
									params: { page: 0, size: 20, sortingColumn: selectedSort, isDescending }
								})
									.unwrap()
									.then((response) => {
										setAccumulatedListViewData(response.data || []);
									})
									.catch((error) => {
										toastError(error?.data?.message || 'Something went wrong!');
									});
							}}
							setIsDescending={setIsDescending}
							isDescending={isDescending}
							onLoadMore={handleListViewLoadMore}
							hasMoreData={hasMoreListViewData}
							isLoadingMore={isListViewLoadingMore}
						/>
					)}

					{/* Card view tab */}
					{activeTab === PROJECT_TAB_VIEW_TYPES.CARD && (
						<ProjectCardsView
							projectCardView={projectCardView?.brandWiseResponses}
							projectLoading={projectLoading}
						/>
					)}

					{isShowCreateProjectModal && (
						<CreateAndEditProjectModal
							onClose={() => {
								dispatch(setIsEditProject(false));
								dispatch(setSelectedProjectToEdit(null));
								dispatch(setIsShowCreateProjectModal(false));
							}}
							isEdit={isEditProject}
							selectedProjectInfo={selectedProjectToEdit}
							onUpdateProjectSuccess={() => {
								setIsShowViewProjectDetailsModal(false);
								setPage(0);
								getAllProjects({
									body: {
										statusList: ['PENDING', 'RUNNING', 'COMPLETED'],
										...getYearBoundaries(currentDate)
									},
									params: {
										page: 0,
										size: activeTab === PROJECT_TAB_VIEW_TYPES.CALENDAR ? 500 : 20
									}
								});
							}}
						/>
					)}
					{isShowDeleteProjectModal && (
						<DeleteConfirmationModal
							onClose={() => dispatch(setIsShowDeleteProjectModal(false))}
							onConfirm={handleConfirmDeleteProject}
							title="Remove Project"
							description="Are you sure about removing this project?"
						/>
					)}
					{isShowViewProjectDetailsModal && (
						<ViewProjectDetailsModal onClose={closeCalendarEvent} projectId={selectedEventId} />
					)}
				</div>
			</section>
		</>
	);
};

export default MyProjectPage;
